import { useEffect, useMemo, useState } from 'react'
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, Timestamp, writeBatch } from 'firebase/firestore'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { db } from '../../firebase'

type OtpStatus = 'pending' | 'sent' | 'failed' | 'verified' | string

interface OtpAttemptRow {
  id: string
  phoneNumber?: string
  status?: OtpStatus
  deviceModel?: string
  osVersion?: string
  platform?: string
  errorCode?: string
  errorMessage?: string
  timestamp?: Timestamp
}

const statusMap: Record<string, { label: string; color: 'default' | 'warning' | 'success' | 'error' }> = {
  pending: { label: 'Pending', color: 'default' },
  sent: { label: 'Sent', color: 'warning' },
  verified: { label: 'Verified', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
}

function formatTimestamp(ts?: Timestamp): string {
  if (!ts) return '-'
  try {
    return ts.toDate().toLocaleString('en-GB')
  } catch (_) {
    return '-'
  }
}

export default function OtpMonitorPage() {
  const [rows, setRows] = useState<OtpAttemptRow[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const pageSize = 20
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [phoneFilter, setPhoneFilter] = useState<string>('')
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  })

  useEffect(() => {
    const q = query(collection(db, 'otp_attempts'), orderBy('timestamp', 'desc'))
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const attempts = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...(docSnap.data() as Omit<OtpAttemptRow, 'id'>),
        }))
        setRows(attempts)
      },
      (error) => {
        console.error('OTP monitor fetch error:', error)
        setSnackbar({
          open: true,
          message: `فشل جلب بيانات OTP Monitor: ${error.message}`,
        })
      },
    )
    return () => unsub()
  }, [])

  const totalFailed = useMemo(() => rows.filter((r) => r.status === 'failed').length, [rows])
  const totalVerified = useMemo(() => rows.filter((r) => r.status === 'verified').length, [rows])
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const statusOk = statusFilter === 'all' ? true : (row.status ?? '') === statusFilter
      const phoneOk = phoneFilter.trim().length === 0
        ? true
        : (row.phoneNumber ?? '').toLowerCase().includes(phoneFilter.trim().toLowerCase())
      return statusOk && phoneOk
    })
  }, [rows, statusFilter, phoneFilter])
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const paginatedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page],
  )
  const selectedCount = selectedIds.size

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const isAllCurrentPageSelected =
    paginatedRows.length > 0 && paginatedRows.every((row) => selectedIds.has(row.id))

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectCurrentPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const row of paginatedRows) {
        if (checked) next.add(row.id)
        else next.delete(row.id)
      }
      return next
    })
  }

  const handleDeleteOne = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'otp_attempts', id))
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      setSnackbar({ open: true, message: 'تم حذف السجل بنجاح.' })
    } catch (error: any) {
      setSnackbar({ open: true, message: `فشل حذف السجل: ${error?.message ?? 'خطأ غير متوقع'}` })
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return
    try {
      const batch = writeBatch(db)
      selectedIds.forEach((id) => {
        batch.delete(doc(db, 'otp_attempts', id))
      })
      await batch.commit()
      setSelectedIds(new Set())
      setSnackbar({ open: true, message: `تم حذف ${selectedCount} سجل بنجاح.` })
    } catch (error: any) {
      setSnackbar({ open: true, message: `فشل الحذف الجماعي: ${error?.message ?? 'خطأ غير متوقع'}` })
    }
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          OTP Monitor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          مراقبة محاولات إرسال رمز التحقق في الوقت الفعلي (بدون التأثير على تجربة المستخدم).
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Chip label={`إجمالي المحاولات: ${rows.length}`} />
        <Chip color="success" label={`Verified: ${totalVerified}`} />
        <Chip color="error" label={`Failed: ${totalFailed}`} />
        <Button
          color="error"
          variant="outlined"
          startIcon={<DeleteIcon />}
          disabled={selectedCount === 0}
          onClick={handleDeleteSelected}
        >
          حذف المحدد ({selectedCount})
        </Button>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          label="فلتر برقم الهاتف"
          value={phoneFilter}
          onChange={(e) => {
            setPhoneFilter(e.target.value)
            setPage(1)
          }}
          size="small"
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="otp-status-filter-label">فلتر بالحالة</InputLabel>
          <Select
            labelId="otp-status-filter-label"
            value={statusFilter}
            label="فلتر بالحالة"
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <MenuItem value="all">الكل</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllCurrentPageSelected}
                  indeterminate={selectedCount > 0 && !isAllCurrentPageSelected}
                  onChange={(e) => toggleSelectCurrentPage(e.target.checked)}
                />
              </TableCell>
              <TableCell>الهاتف</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>نوع الجهاز والنظام</TableCell>
              <TableCell>كود الخطأ التقني</TableCell>
              <TableCell>وقت المحاولة</TableCell>
              <TableCell align="center">إجراء</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => {
              const statusMeta = statusMap[row.status ?? ''] ?? { label: row.status ?? '-', color: 'default' as const }
              const deviceText = [row.deviceModel, row.platform, row.osVersion].filter(Boolean).join(' | ') || '-'
              const errorText = row.status === 'failed' && row.errorCode
                ? `${row.errorCode}${row.errorMessage ? `: ${row.errorMessage}` : ''}`
                : '-'
              return (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selectedIds.has(row.id)} onChange={() => toggleSelectRow(row.id)} />
                  </TableCell>
                  <TableCell>{row.phoneNumber || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
                  </TableCell>
                  <TableCell>{deviceText}</TableCell>
                  <TableCell sx={{ maxWidth: 400, wordBreak: 'break-word' }}>{errorText}</TableCell>
                  <TableCell>{formatTimestamp(row.timestamp)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="حذف السجل">
                      <IconButton color="error" size="small" onClick={() => handleDeleteOne(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  لا توجد محاولات OTP حتى الآن.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="center">
        <Pagination
          color="primary"
          page={page}
          count={totalPages}
          onChange={(_, value) => setPage(value)}
        />
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity="error" variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
